// Atoms
import { Image } from "components/Image";
import { Video } from "components/Video";

// Molecules
import { Heading } from "components/Heading";
import { Field } from "components/Field";
import { Input } from "components/Input";
import { InputNumber } from "components/InputNumber";
import { InputOtp } from "components/InputOtp";
import { InputUpload } from "components/InputUpload";
import { Textarea } from "components/Textarea";
import { Checkbox, CheckboxGroup } from "components/Checkbox";
import { Radio, RadioGroup } from "components/Radio";
import { Switch } from "components/Switch";
import { Button } from "components/Button";
import { Group } from "components/Group";
import { Label } from "components/Label";

// Accordion
import { Accordion, AccordionItem, AccordionHeader, AccordionContent } from "components/Accordion";
// Alert
import { Alert } from "components/Alert";
// Card
import { Card, CardHeader, CardContent, CardFooter } from "components/Card";
// Dialog
import { Dialog } from "components/Dialog";
// Drawer
import { Drawer } from "components/Drawer";
// Splitter
import { Splitter, SplitterPanel, SplitterHandle } from "components/Splitter";
// Floating
import { Floating, FloatingArrow } from "components/Floating";
// Table
import { Table, TableHead, TableBody, TableFooter, TableRow, TableCell, TableHeaderCell } from "components/Table";
// Icon
import { Icon } from "components/Icon";
// Listbox
import { Listbox, ListboxContent, ListboxItem, ListboxGroup } from "components/Listbox";
// Chat
import { Chat, ChatGroup, ChatBubble, ChatDivider } from "components/Chat";
// Skeleton
import { Skeleton } from "components/Skeleton";
// Loader
import { Loader } from "components/Loader";
// Select
import { Select, SelectTrigger, SelectContent, SelectGroup, SelectItem, SelectIndicator } from "components/Select";
// Combobox
import {
  Combobox,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxItem,
  ComboboxGroup,
  ComboboxItemIndicator,
  ComboboxSeparator,
  ComboboxEmpty,
} from "components/Combobox";
// VisuallyHidden

import { Menu, MenuItem } from "components/Menu";
// Toast
import { Toaster, Toast } from "components/Toast";
import QrCode from "components/QrCode/QrCode.vue";
import { Editor, EditorHeader, type EditorProps } from "components/Editor";

// Organisms
import { Layout, LayoutApp, LayoutLogin } from "components/Layout";
import Debug from "components/Debug/Debug.vue";

// New component structure
import { EPrimitive } from "components/Primitive";
import { Kbd } from "components/Kbd";
import { Chip } from "components/Chip";
import { Placeholder } from "components/Placeholder";

// Tab
import { Tab, TabList, TabTrigger, TabContent } from "components/Tab";

// Styles
import "tailwindcss/index.css";
import "../assets/css/main.css";
import "../assets/css/elements.css";

import { createPinia } from "pinia";
import { defineEConnectElement } from "./defineEConnectElementsApi";
import { defaultElementOptions } from "./eConnectElementOptions";

export type {
  ElementDefinition,
  ElementDoc,
  EventDoc,
  PropDoc,
  StoryDoc,
  StyleDoc,
  ComposableDefinition,
  ComposableExample,
} from "shared";

export { elementDefinitions, elementDefinitionsBySlug, initDefinitions } from "./elements-metadata";
export { composableDefinitions, composableDefinitionsBySlug, initComposableDefinitions } from "./composables-metadata";

/** Shared Pinia instance used by the EConnect components. */
const pinia = createPinia();

/* -------------------------------------------------------------------------- */
/*  Component definitions                                                     */
/* -------------------------------------------------------------------------- */

// Atoms
const IconElement = defineEConnectElement(Icon, defaultElementOptions);
const ImageElement = defineEConnectElement(Image, defaultElementOptions);
const VideoElement = defineEConnectElement(Video, defaultElementOptions);
const KbdElement = defineEConnectElement(Kbd, defaultElementOptions);
const ChipElement = defineEConnectElement(Chip, defaultElementOptions);
const PlaceholderElement = defineEConnectElement(Placeholder, defaultElementOptions);

// Tab
const tabComponent = defineEConnectElement(Tab, defaultElementOptions);
const tabListComponent = defineEConnectElement(TabList, defaultElementOptions);
const tabTriggerComponent = defineEConnectElement(TabTrigger, defaultElementOptions);
const tabContentComponent = defineEConnectElement(TabContent, defaultElementOptions);

// Molecules
const ButtonElement = defineEConnectElement(Button, {
  ...defaultElementOptions,
  formSubmitter: true,
  customElementOptions: {
    shadowRoot: true,
    shadowRootOptions: { delegatesFocus: true },
  },
});
const GroupElement = defineEConnectElement(Group, defaultElementOptions);
const LabelElement = defineEConnectElement(Label, defaultElementOptions);
const HeadingElement = defineEConnectElement(Heading, defaultElementOptions);
const FieldElement = defineEConnectElement(Field, defaultElementOptions);
const InputElement = defineEConnectElement(Input, { ...defaultElementOptions, formAssociated: true });
const InputNumberElement = defineEConnectElement(InputNumber, { ...defaultElementOptions, formAssociated: true });
const InputOtpElement = defineEConnectElement(InputOtp, { ...defaultElementOptions, formAssociated: true });
const InputUploadElement = defineEConnectElement(InputUpload, { ...defaultElementOptions, formAssociated: true });
const TextareaElement = defineEConnectElement(Textarea, { ...defaultElementOptions, formAssociated: true });

// Checkbox
const CheckboxElement = defineEConnectElement(Checkbox, { ...defaultElementOptions, formAssociated: true });
const CheckboxGroupElement = defineEConnectElement(CheckboxGroup, defaultElementOptions);

// Radio
const RadioElement = defineEConnectElement(Radio, defaultElementOptions);
const RadioGroupElement = defineEConnectElement(RadioGroup, { ...defaultElementOptions, formAssociated: true });

// Switch
const SwitchElement = defineEConnectElement(Switch, { ...defaultElementOptions, formAssociated: true });

const MenuItemElement = defineEConnectElement(MenuItem, defaultElementOptions);

// Organisms
const LayoutElement = defineEConnectElement(Layout, defaultElementOptions);
const LayoutAppElement = defineEConnectElement(LayoutApp, defaultElementOptions);
const LayoutLoginElement = defineEConnectElement(LayoutLogin, defaultElementOptions);
const MenuElement = defineEConnectElement(Menu, defaultElementOptions);
const DebugElement = defineEConnectElement(Debug, defaultElementOptions);

// Accordion
const AccordionElement = defineEConnectElement(Accordion, defaultElementOptions);
const AccordionItemElement = defineEConnectElement(AccordionItem, defaultElementOptions);
const AccordionHeaderElement = defineEConnectElement(AccordionHeader, defaultElementOptions);
const AccordionContentElement = defineEConnectElement(AccordionContent, defaultElementOptions);

// Alert
const AlertElement = defineEConnectElement(Alert, defaultElementOptions);

// Card
const CardElement = defineEConnectElement(Card, defaultElementOptions);
const CardHeaderElement = defineEConnectElement(CardHeader, defaultElementOptions);
const CardContentElement = defineEConnectElement(CardContent, defaultElementOptions);
const CardFooterElement = defineEConnectElement(CardFooter, defaultElementOptions);

// Chat
const ChatElement = defineEConnectElement(Chat, defaultElementOptions);
const ChatGroupElement = defineEConnectElement(ChatGroup, defaultElementOptions);
const ChatBubbleElement = defineEConnectElement(ChatBubble, defaultElementOptions);
const ChatDividerElement = defineEConnectElement(ChatDivider, defaultElementOptions);

// Skeleton
const SkeletonElement = defineEConnectElement(Skeleton, defaultElementOptions);

// Loader
const LoaderElement = defineEConnectElement(Loader, defaultElementOptions);

// Dialog
const DialogElement = defineEConnectElement(Dialog, defaultElementOptions);

// Drawer
const DrawerElement = defineEConnectElement(Drawer, defaultElementOptions);

// Splitter
const SplitterElement = defineEConnectElement(Splitter, defaultElementOptions);
const SplitterPanelElement = defineEConnectElement(SplitterPanel, defaultElementOptions);
const SplitterHandleElement = defineEConnectElement(SplitterHandle, defaultElementOptions);

// Floating
const FloatingElement = defineEConnectElement(Floating, defaultElementOptions);
const FloatingArrowElement = defineEConnectElement(FloatingArrow, defaultElementOptions);

// Table
const TableElement = defineEConnectElement(Table, defaultElementOptions);
const TableHeadElement = defineEConnectElement(TableHead, defaultElementOptions);
const TableBodyElement = defineEConnectElement(TableBody, defaultElementOptions);
const TableFooterElement = defineEConnectElement(TableFooter, defaultElementOptions);
const TableRowElement = defineEConnectElement(TableRow, defaultElementOptions);
const TableCellElement = defineEConnectElement(TableCell, defaultElementOptions);
const TableHeaderCellElement = defineEConnectElement(TableHeaderCell, defaultElementOptions);

// Listbox
const ListboxElement = defineEConnectElement(Listbox, { ...defaultElementOptions, formAssociated: true });
const ListboxContentElement = defineEConnectElement(ListboxContent, defaultElementOptions);
const ListboxItemElement = defineEConnectElement(ListboxItem, defaultElementOptions);
const ListboxGroupElement = defineEConnectElement(ListboxGroup, defaultElementOptions);

// Select
const SelectElement = defineEConnectElement(Select, { ...defaultElementOptions, formAssociated: true });
const SelectTriggerElement = defineEConnectElement(SelectTrigger, defaultElementOptions);
const SelectContentElement = defineEConnectElement(SelectContent, defaultElementOptions);
const SelectGroupElement = defineEConnectElement(SelectGroup, defaultElementOptions);
const SelectItemElement = defineEConnectElement(SelectItem, defaultElementOptions);
const SelectIndicatorElement = defineEConnectElement(SelectIndicator, defaultElementOptions);

// Combobox
const ComboboxElement = defineEConnectElement(Combobox, { ...defaultElementOptions, formAssociated: true });
const ComboboxInputElement = defineEConnectElement(ComboboxInput, defaultElementOptions);
const ComboboxTriggerElement = defineEConnectElement(ComboboxTrigger, defaultElementOptions);
const ComboboxContentElement = defineEConnectElement(ComboboxContent, defaultElementOptions);
const ComboboxItemElement = defineEConnectElement(ComboboxItem, defaultElementOptions);
const ComboboxGroupElement = defineEConnectElement(ComboboxGroup, defaultElementOptions);
const ComboboxItemIndicatorElement = defineEConnectElement(ComboboxItemIndicator, defaultElementOptions);
const ComboboxSeparatorElement = defineEConnectElement(ComboboxSeparator, defaultElementOptions);
const ComboboxEmptyElement = defineEConnectElement(ComboboxEmpty, defaultElementOptions);

// Toast
const ToasterElement = defineEConnectElement(Toaster, defaultElementOptions);
const ToastElement = defineEConnectElement(Toast, defaultElementOptions);

// QR Code
const QrCodeElement = defineEConnectElement(QrCode, defaultElementOptions);

// Editor
const EditorElement = defineEConnectElement(Editor, { ...defaultElementOptions, formAssociated: true });
const EditorHeaderElement = defineEConnectElement(EditorHeader, defaultElementOptions);

/**
 * Public type for the `<e-editor>` custom element instance.
 * Includes both the underlying element instance and its props.
 */
export type EEditorElement = InstanceType<typeof EditorElement> & {
  [K in keyof EditorProps]?: EditorProps[K];
};

/* -------------------------------------------------------------------------- */
/*  Public exports                                                            */
/* -------------------------------------------------------------------------- */

export {
  // Primitive
  // eslint-disable-next-line unicorn/prefer-export-from -- also used locally below in defineCustomElement
  EPrimitive,

  // Atoms
  ImageElement,
  VideoElement,
  KbdElement,
  ChipElement,
  PlaceholderElement,

  // Tab
  tabComponent,
  tabListComponent,
  tabTriggerComponent,
  tabContentComponent,

  // Molecules
  ButtonElement,
  GroupElement,
  LabelElement,
  HeadingElement,
  FieldElement,
  InputElement,
  InputNumberElement,
  InputOtpElement,
  InputUploadElement,
  TextareaElement,
  // Organisms
  LayoutElement,
  LayoutAppElement,
  LayoutLoginElement,
  DebugElement,
  // Accordion
  AccordionElement,
  AccordionItemElement,
  AccordionHeaderElement,
  AccordionContentElement,
  // Alert
  AlertElement,
  // Card
  CardElement,
  CardHeaderElement,
  CardContentElement,
  CardFooterElement,
  // Chat
  ChatElement,
  ChatGroupElement,
  ChatBubbleElement,
  ChatDividerElement,
  // Skeleton
  SkeletonElement,
  // Loader
  LoaderElement,
  // Dialog
  DialogElement,
  // Drawer
  DrawerElement,
  // Splitter
  SplitterElement,
  SplitterPanelElement,
  SplitterHandleElement,
  // Floating
  FloatingElement,
  FloatingArrowElement,
  // Table
  TableElement,
  TableHeadElement,
  TableBodyElement,
  TableFooterElement,
  TableRowElement,
  TableCellElement,
  TableHeaderCellElement,
  // Listbox
  ListboxElement,
  ListboxContentElement,
  ListboxItemElement,
  ListboxGroupElement,
  // Select
  SelectElement,
  SelectTriggerElement,
  SelectContentElement,
  SelectGroupElement,
  SelectItemElement,
  SelectIndicatorElement,
  // Checkbox
  CheckboxElement,
  CheckboxGroupElement,
  // Radio
  RadioElement,
  RadioGroupElement,
  // Switch
  SwitchElement,
  // Combobox
  ComboboxElement,
  ComboboxInputElement,
  ComboboxTriggerElement,
  ComboboxContentElement,
  ComboboxItemElement,
  ComboboxGroupElement,
  ComboboxItemIndicatorElement,
  ComboboxSeparatorElement,
  ComboboxEmptyElement,
  // Menu Elements
  MenuElement,
  MenuItemElement,
  // Icon Elements
  IconElement,
  // Other elements
  QrCodeElement,
  EditorElement,
  EditorHeaderElement,
  // Toast
  ToasterElement,
  ToastElement,

  // VisuallyHidden (internal use)

  // Composables / utilities
  pinia,
};

export { TailwindCssPluginPart } from "../style-engine/tailwind-parts";
export { setRouterBase } from "components/Menu/useMenu";
export {
  useStateStore,
  getElementByUid,
  getElementUid,
  registerElement,
  unregisterElement,
} from "composables/useStateStore";
export { useAppearance, APPEARANCE_STORAGE_KEY } from "composables/useAppearance";
export type { AppearanceMode, UseAppearanceOptions, UseAppearanceReturn } from "composables/useAppearance";
export { usePosition } from "composables/usePosition";
export { useTypeahead } from "composables/useTypeahead";
export {
  useLogoUrl,
  buildVariantSegment,
  buildColorSegment,
  buildModeSegment,
  buildLogoUrl,
} from "composables/useLogoUrl";
export type { UseLogoUrlOptions, UseLogoUrlReturn, LogoVariant, LogoColor } from "composables/useLogoUrl";
export {
  useSplitterGroup,
  useSplitterPanel,
  useSplitterHandle,
  createSplitterCore,
  type UseSplitterGroupOptions,
  type UseSplitterGroupReturn,
  type UseSplitterPanelOptions,
  type UseSplitterPanelReturn,
  type UseSplitterHandleOptions,
  type UseSplitterHandleReturn,
  type SplitterCoreOptions,
  type SplitterCore,
} from "composables/useSplitter";
export {
  useCollectionItem,
  selectionHelpers,
  highlightHelpers,
  type CollectionItemBaseProps,
  type CollectionBaseProps,
  type UseCollectionItemOptions,
  type UseCollectionItemReturn,
} from "composables/useCollectionItem";
export { useLayout } from "components/Layout";
export type { LayoutProps, LayoutAppProps, LayoutLoginProps } from "components/Layout";
export { createEventDispatcher } from "@/runtime/shared/events";
export { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
export { registerIconProvider, type IconProvider } from "composables/iconProviders";
export { defineEConnectElement } from "./defineEConnectElementsApi";
export { defaultElementOptions } from "./eConnectElementOptions";
export { setupTurboLifecycle } from "./turboLifecycle";
export { type VisuallyHiddenInputProps, VisuallyHiddenInput } from "components/VisuallyHidden";

/**
 * Clears all component state from the Pinia store.
 *
 * **When to use**:
 * - SPA frameworks that reuse the same DOM/JavaScript context between pages
 * - Prevents stale component references and memory leaks during navigation
 *
 * **When NOT needed**:
 * - Full page reloads (browser clears everything automatically)
 * - Server-rendered pages without client-side navigation
 * - Individual component cleanup (handled automatically by disconnectedCallback)
 *
 * @example
 * // Vue Router
 * router.beforeEach(() => {
 *   clearEConnectState();
 * });
 *
 * @example
 * // React Router
 * function App() {
 *   useEffect(() => {
 *     return () => clearEConnectState();
 *   }, [location.pathname]);
 * }
 */
export const clearEConnectState = (): void => {
  try {
    // The Pinia store ID is "storedProps" and the reactive state ref is "state"
    const storeState = pinia?.state?.value?.storedProps?.state as Record<string, unknown> | undefined;
    if (storeState && typeof storeState === "object") {
      for (const key of Object.keys(storeState)) {
        delete storeState[key];
      }
    }
  } catch (error) {
    console.warn("Failed to clear EConnect state:", error);
  }
};

/* -------------------------------------------------------------------------- */
/*  Custom element registration                                               */
/* -------------------------------------------------------------------------- */

/**
 * Defines a custom element if it has not been defined yet.
 *
 * @param tag - Custom element tag name.
 * @param ctor - Constructor for the custom element.
 */
const define = (tag: string, ctor: CustomElementConstructor): void => {
  if (!customElements.get(tag)) {
    customElements.define(tag, ctor);
  }
};

/** All custom element tag-to-constructor mappings. */
const ELEMENT_REGISTRY: [string, CustomElementConstructor][] = [
  // Primitive
  ["e-primitive", EPrimitive],

  // Atoms
  ["e-icon", IconElement],
  ["e-image", ImageElement],
  ["e-video", VideoElement],
  ["e-kbd", KbdElement],
  ["e-chip", ChipElement],
  ["e-placeholder", PlaceholderElement],
  // Tab
  ["e-tab", tabComponent],
  ["e-tab-list", tabListComponent],
  ["e-tab-trigger", tabTriggerComponent],
  ["e-tab-content", tabContentComponent],
  // Molecules
  ["e-input", InputElement],
  ["e-input-number", InputNumberElement],
  ["e-input-otp", InputOtpElement],
  ["e-input-upload", InputUploadElement],
  ["e-textarea", TextareaElement],
  ["e-button", ButtonElement],
  ["e-group", GroupElement],
  ["e-label", LabelElement],
  ["e-heading", HeadingElement],
  ["e-field", FieldElement],
  ["e-menu-item", MenuItemElement],
  // Accordion
  ["e-accordion", AccordionElement],
  ["e-accordion-item", AccordionItemElement],
  ["e-accordion-header", AccordionHeaderElement],
  ["e-accordion-content", AccordionContentElement],
  // Alert
  ["e-alert", AlertElement],
  // Card
  ["e-card", CardElement],
  ["e-card-header", CardHeaderElement],
  ["e-card-content", CardContentElement],
  ["e-card-footer", CardFooterElement],
  // Chat
  ["e-chat", ChatElement],
  ["e-chat-group", ChatGroupElement],
  ["e-chat-bubble", ChatBubbleElement],
  ["e-chat-divider", ChatDividerElement],
  // Skeleton
  ["e-skeleton", SkeletonElement],
  // Loader
  ["e-loader", LoaderElement],
  // Dialog
  ["e-dialog", DialogElement],
  // Drawer
  ["e-drawer", DrawerElement],
  // Splitter
  ["e-splitter", SplitterElement],
  ["e-splitter-panel", SplitterPanelElement],
  ["e-splitter-handle", SplitterHandleElement],
  // Floating
  ["e-floating", FloatingElement],
  ["e-floating-arrow", FloatingArrowElement],
  // Table
  ["e-table", TableElement],
  ["e-table-head", TableHeadElement],
  ["e-table-body", TableBodyElement],
  ["e-table-footer", TableFooterElement],
  ["e-table-row", TableRowElement],
  ["e-table-cell", TableCellElement],
  ["e-table-header-cell", TableHeaderCellElement],
  // Listbox
  ["e-listbox", ListboxElement],
  ["e-listbox-content", ListboxContentElement],
  ["e-listbox-item", ListboxItemElement],
  ["e-listbox-group", ListboxGroupElement],
  // Select
  ["e-select", SelectElement],
  ["e-select-trigger", SelectTriggerElement],
  ["e-select-content", SelectContentElement],
  ["e-select-group", SelectGroupElement],
  ["e-select-item", SelectItemElement],
  ["e-select-indicator", SelectIndicatorElement],
  // Checkbox
  ["e-checkbox", CheckboxElement],
  ["e-checkbox-group", CheckboxGroupElement],
  // Radio
  ["e-radio", RadioElement],
  ["e-radio-group", RadioGroupElement],
  // Switch
  ["e-switch", SwitchElement],
  // Combobox
  ["e-combobox", ComboboxElement],
  ["e-combobox-input", ComboboxInputElement],
  ["e-combobox-trigger", ComboboxTriggerElement],
  ["e-combobox-content", ComboboxContentElement],
  ["e-combobox-item", ComboboxItemElement],
  ["e-combobox-group", ComboboxGroupElement],
  ["e-combobox-item-indicator", ComboboxItemIndicatorElement],
  ["e-combobox-seperator", ComboboxSeparatorElement],
  ["e-combobox-empty", ComboboxEmptyElement],
  // Other
  ["e-qr-code", QrCodeElement],
  ["e-layout", LayoutElement],
  ["e-layout-app", LayoutAppElement],
  ["e-layout-login", LayoutLoginElement],
  ["e-menu", MenuElement],
  ["e-debug", DebugElement],
  ["e-editor", EditorElement],
  ["e-editor-header", EditorHeaderElement],
  // Toast
  ["e-toaster", ToasterElement],
  ["e-toast", ToastElement],
];

/**
 * Registers all EConnect custom elements with the browser's
 * {@link customElements} registry.
 *
 * Safe to call multiple times; existing definitions are left untouched.
 */
export const register = (): void => {
  for (const [tag, ctor] of ELEMENT_REGISTRY) {
    define(tag, ctor);
  }
};

/* -------------------------------------------------------------------------- */
/*  Vue global component typings                                              */
/* -------------------------------------------------------------------------- */

declare module "vue" {
  export interface GlobalComponents {
    // Primitive
    EPrimitive: typeof EPrimitive;

    // Atoms
    Icon: typeof IconElement;
    EImage: typeof ImageElement;
    EVideo: typeof VideoElement;
    EKbd: typeof KbdElement;
    EChip: typeof ChipElement;
    EPlaceholder: typeof PlaceholderElement;

    // Tab
    ETab: typeof tabComponent;
    ETabList: typeof tabListComponent;
    ETabTrigger: typeof tabTriggerComponent;
    ETabContent: typeof tabContentComponent;

    // Molecules
    EInput: typeof InputElement;
    EInputNumber: typeof InputNumberElement;
    EInputOtp: typeof InputOtpElement;
    EInputUpload: typeof InputUploadElement;
    ETextarea: typeof TextareaElement;
    EButton: typeof ButtonElement;
    EGroup: typeof GroupElement;
    ELabel: typeof LabelElement;
    EHeading: typeof HeadingElement;
    EField: typeof FieldElement;
    EMenuItem: typeof MenuItemElement;
    // Organisms
    ELayout: typeof LayoutElement;
    ELayoutApp: typeof LayoutAppElement;
    ELayoutLogin: typeof LayoutLoginElement;
    EMenu: typeof MenuElement;
    EDebug: typeof DebugElement;
    // Accordion
    EAccordion: typeof AccordionElement;
    EAccordionItem: typeof AccordionItemElement;
    EAccordionHeader: typeof AccordionHeaderElement;
    EAccordionContent: typeof AccordionContentElement;
    // Alert
    EAlert: typeof AlertElement;
    // Card
    ECard: typeof CardElement;
    ECardHeader: typeof CardHeaderElement;
    ECardContent: typeof CardContentElement;
    ECardFooter: typeof CardFooterElement;
    // Chat
    EChat: typeof ChatElement;
    EChatGroup: typeof ChatGroupElement;
    EChatBubble: typeof ChatBubbleElement;
    EChatDivider: typeof ChatDividerElement;
    // Skeleton
    ESkeleton: typeof SkeletonElement;
    // Loader
    ELoader: typeof LoaderElement;
    // Dialog
    EDialog: typeof DialogElement;
    // Drawer
    EDrawer: typeof DrawerElement;
    // Splitter
    ESplitter: typeof SplitterElement;
    ESplitterPanel: typeof SplitterPanelElement;
    ESplitterHandle: typeof SplitterHandleElement;
    // Floating
    EFloating: typeof FloatingElement;
    EFloatingArrow: typeof FloatingArrowElement;
    // Table
    ETable: typeof TableElement;
    ETableHead: typeof TableHeadElement;
    ETableBody: typeof TableBodyElement;
    ETableFooter: typeof TableFooterElement;
    ETableRow: typeof TableRowElement;
    ETableCell: typeof TableCellElement;
    ETableHeaderCell: typeof TableHeaderCellElement;
    // Listbox
    EListbox: typeof ListboxElement;
    EListboxContent: typeof ListboxContentElement;
    EListboxItem: typeof ListboxItemElement;
    EListboxGroup: typeof ListboxGroupElement;
    // Checkbox
    ECheckbox: typeof CheckboxElement;
    ECheckboxGroup: typeof CheckboxGroupElement;
    // Radio
    ERadio: typeof RadioElement;
    ERadioGroup: typeof RadioGroupElement;
    // Switch
    ESwitch: typeof SwitchElement;
    // Select
    ESelect: typeof SelectElement;
    ESelectTrigger: typeof SelectTriggerElement;
    ESelectContent: typeof SelectContentElement;
    ESelectGroup: typeof SelectGroupElement;
    ESelectItem: typeof SelectItemElement;
    ESelectIndicator: typeof SelectIndicatorElement;
    // Toast
    EToaster: typeof ToasterElement;
    EToast: typeof ToastElement;
    // Other elements
    EQrCode: typeof QrCodeElement;
    EEditor: typeof EditorElement;
    EEditorHeader: typeof EditorHeaderElement;
  }
}

export { toast } from "components/Toast";
